/**
 * Auth store (Zustand).
 *
 * Holds the session JWT + user, persists the token to localStorage under the
 * SAME key the http wrapper reads (`rt.auth.token`), and exposes login/logout.
 * `isAuthenticated` is derived from token presence.
 */

import { create } from 'zustand';
import type { AuthUser, TokenResponse } from '@/features/auth/authApi';

const TOKEN_KEY = 'rt.auth.token';

function persistToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode) — degrade to in-memory only */
  }
}

function readPersistedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Persist a successful auth exchange. */
  login: (result: TokenResponse) => void;
  /** Update the cached user (e.g. after GET /me). */
  setUser: (user: AuthUser) => void;
  /** Clear token + user and wipe persistence. */
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => {
  const token = readPersistedToken();
  return {
    token,
    user: null,
    isAuthenticated: token !== null,
    login: ({ token: newToken, user }) => {
      persistToken(newToken);
      set({ token: newToken, user, isAuthenticated: true });
    },
    setUser: (user) => set({ user }),
    logout: () => {
      persistToken(null);
      set({ token: null, user: null, isAuthenticated: false });
    },
  };
});
